import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, combineLatest, throwError } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';

import { environment } from '@env';
import { RegionReference, RegionReferenceResponse, RegionSteps } from '@shared/models';
import { BackendService } from '@shared/services/backend.service';
import { fromAuth } from '@shared/store';
import { ErrorUtils } from '@shared/utils';

@Injectable({
  providedIn: 'root',
})
export class RegionService {
  private activeRegions$?: Observable<RegionReference[]>;

  constructor(
    private backendService: BackendService,
    private store: Store,
  ) {}

  // Cached for the session; an empty list on failure makes callers behave as a single-region tenant, as today.
  public getActiveRegions(): Observable<RegionReference[]> {
    this.activeRegions$ ??= this.backendService.get<RegionReferenceResponse>(`${environment.accountFlowService}/v1/reference/regions`).pipe(
      map(({ regions }) => regions ?? []),
      catchError((errorRes) => ErrorUtils.catchError('regionService.getActiveRegions', errorRes).pipe(map(() => []))),
      shareReplay(1),
    );

    return this.activeRegions$;
  }

  // Regions the tenant may use: enabled on the account and still ACTIVE. Absent or empty enabledRegions means NA, as on older backends.
  public getTenantRegions(): Observable<RegionReference[]> {
    return combineLatest([this.store.select(fromAuth.selectBusinessAccountDetails), this.getActiveRegions()]).pipe(
      map(([account, active]) => {
        const enabled = account?.enabledRegions?.length ? account.enabledRegions : ['NA'];
        return active.filter(({ code }) => enabled.includes(code));
      }),
    );
  }

  public getRegionSteps(code: string): Observable<RegionSteps> {
    return this.backendService
      .get<RegionSteps>(`${environment.linxRegionsEndpoint}/${code}/steps`)
      .pipe(
        catchError((errorRes) =>
          ErrorUtils.catchError('regionService.getRegionSteps', errorRes).pipe(switchMap(() => throwError(() => errorRes))),
        ),
      );
  }
}
